import moment from 'moment'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { CreateAccommodationItemViewModel } from './models'
import type { CreateItemProps } from './utils'
import {
  createAccommodationPayloadFromViewModel,
} from './utils'
import AccommodationFields from '../Common/AccommodationFields'
import { formOptions } from '../Common/Fields/config'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

import {
  addToTripSaveErrorEvent,
  addToTripSaveEvent,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'

function CreateAccommodation(props: CreateItemProps) {
  const { tripId, defaultDate, rankSpec } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<CreateAccommodationItemViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'ACCOMMODATION',
      sourceType: 'NA',
      isBooked: false,
      startDate: defaultDate,
      endDate: defaultDate ?
          moment(defaultDate).add(1, 'day').clone()?.format(ISO_DATE_FORMAT) :
        undefined,
      place: {},
    },
  })
  const { handleSubmit } = methods

  const onInvalid = useCallback(() => {
    fireInteractionEvent(addToTripSaveErrorEvent('accommodation_custom'))
  }, [])

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: (res) => {
      fireInteractionEvent(addToTripSaveEvent('accommodation_custom'))
      scrollToNewItemWithRetry(res.id)
      closeModal()
    },
    onError: onInvalid,
  })

  const createAccommodationItem = useCallback(
    (viewModel: CreateAccommodationItemViewModel) => {
      createMutate({
        tripId,
        tripItem: createAccommodationPayloadFromViewModel(viewModel),
        rankSpec,
      })
    },
    [createMutate, tripId, rankSpec],
  )

  const modalTitle = intl.formatMessage({
    id: '-Bu30VZQVSTExhy6eAPU',
    defaultMessage: 'Add your accommodation',
    description: 'Modal title when adding accommodation to trip in the trip planner item editor modal',
  })
  const saveLabel = intl.formatMessage({
    id: '1W7dLIxLSatmNlVreDYG',
    defaultMessage: 'Save',
    description: 'Save button label in the add accommodation modal in the trip planner item editor modal',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <AccommodationFields tripId={tripId} />
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createAccommodationItem, onInvalid),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>

      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateAccommodation
